import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useState
} from 'react';
import { Card } from '@braze/web-sdk';
import { useBraze } from 'modules/push-notification';
import { FeatureFlag, useFeatureToggle } from 'modules/unleash';
import { ContentCard } from './types';
import { movePinnedCardsToTop } from './utils';

export interface ContentCardsContextType {
  contentCards: ContentCard[];
  logCardClick: (card: Card) => void;
  logCardDismissal: (card: Card) => void;
}

export const ContentCardsContext =
  createContext<ContentCardsContextType | null>(null);

interface ContentCardsProviderProps {
  children: ReactNode;
}

export const ContentCardsProvider: FC<ContentCardsProviderProps> = ({
  children
}) => {
  const { isEnabled } = useFeatureToggle(
    FeatureFlag.PRO_TALENT_IS_CONTENT_CARDS_ENABLED
  );
  const braze = useBraze();
  const [contentCards, setContentCards] = useState<ContentCard[]>([]);

  const logCardClick = (card: Card) => {
    if (!braze) return;
    braze.logCardClick(card);
    console.log('Content card clicked:', card);
  };

  const logCardDismissal = (card: Card) => {
    if (!braze) return;
    braze.logCardDismissal(card);
    setContentCards(prev => prev.filter(c => c.id !== card.id));
    console.log('Content card dismissed:', card);
  };

  const logContentCardImpressions = useCallback(
    (cards: Card[]) => {
      if (!braze) return;
      const newCards = cards.filter(card => !card.viewed);
      if (newCards.length > 0) {
        braze.logContentCardImpressions(newCards);
        console.log('Content card impressions logged:', cards);
      }
    },
    [braze]
  );

  useEffect(() => {
    if (!braze || !isEnabled) return;
    console.log('Setting up content cards subscription...');

    const subscriptionId = braze.subscribeToContentCardsUpdates(update => {
      console.log('Content Cards received:', update);

      const sortedCards = movePinnedCardsToTop(update.cards);
      setContentCards(sortedCards as ContentCard[]);

      logContentCardImpressions(sortedCards);
    });

    braze.requestContentCardsRefresh();

    return () => {
      if (subscriptionId) {
        braze.removeSubscription(subscriptionId);
      }
    };
  }, [braze, isEnabled, logContentCardImpressions]);

  const contextValue: ContentCardsContextType = {
    contentCards,
    logCardClick,
    logCardDismissal
  };

  return (
    <ContentCardsContext.Provider value={contextValue}>
      {children}
    </ContentCardsContext.Provider>
  );
};
